import { useState, useEffect, useCallback, Suspense, lazy, Fragment } from "react";
import './App.css';
import FlashCard from "./FlashCard";
import Greeting from "./Greeting";
import { IconCards, IconExam, IconGuide, IconBolt, IconMore, IconMoon, IconSun, IconLock } from "./Icons";
import { sections } from "./studyGuideData";
import PremiumModal from "./PremiumModal";
import TestimonialsCarousel from "./TestimonialsCarousel";
import LazyCardWrapper from "./LazyCardWrapper";
import Pricing from "./pages/Pricing";
import StudyGuideIndex from "./pages/StudyGuideIndex";
import BritishHistory from "./pages/BritishHistory";
import CultureAndTraditions from "./pages/CultureAndTraditions";
import PatronSaintsAndSymbols from "./pages/PatronSaintsAndSymbols";
import FestivalsAndCelebrations from "./pages/FestivalsAndCelebrations";
import SportsAndTraditions from "./pages/SportsAndTraditions";
import ModernBritain from "./pages/ModernBritain";
import HumanRightsAndCitizenship from "./pages/HumanRightsAndCitizenship";
import TaxationAndDriving from "./pages/TaxationAndDriving";
import BritainInTheWorld from "./pages/BritainInTheWorld";
import MonarchsTimeline from "./pages/MonarchsTimeline";
import PeopleMatrix from "./pages/PeopleMatrix";
import InventionsTimeline from "./pages/InventionsTimeline";
import KeyDatesTimeline from "./pages/KeyDatesTimeline";
import GovernmentAndLaw from "./pages/GovernmentAndLaw";
import ElectionsCheatSheet from "./pages/ElectionsCheatSheet";
import DevolvedNations from "./pages/DevolvedNations";
import CourtHierarchy from "./pages/CourtHierarchy";
import ILRGuide from "./pages/ILRGuide";
import GuidePage from "./pages/GuidePage";
import CheatSheet from "./pages/CheatSheet";
import FreeCheatSheet from "./pages/FreeCheatSheet";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Disclaimer from "./pages/Disclaimer";
import { guideBySlug } from "./pages/immigrationGuides/index";
import TestDatePicker from "./TestDatePicker";
import { calculateConfidenceScore } from "./confidenceScore";
import ProgressGraph from "./ProgressGraph";
import ErrorBoundary from "./ErrorBoundary";
import { useDarkMode } from "./useDarkMode";
import useDocumentMeta from "./useDocumentMeta";
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigationType } from "react-router-dom"; // Import react-router-dom components
// jspdf + jspdf-autotable are imported dynamically at the point of use, not
// here. As static imports they sat in the main bundle, so every visitor
// downloaded ~45KB of PDF library that only a premium user clicking "download
// cheat sheet" ever runs. Lighthouse flagged them as unused JavaScript on
// 2026-09-09. See handleDownloadCheatSheet below.
import confetti from "canvas-confetti";
import { safeGetItem, safeSetItem } from "./safeStorage";
import { track } from "./analytics";
import { generateCheatSheet } from "./utils/generateCheatSheet";

// Code-split: both pull in mockExamsData.js (14k+ lines), so keep them
// out of the main bundle — most visitors (SEO/guide-page traffic) never touch either.
const MockExam = lazy(() => import("./MockExam"));
const QuickFireChallenge = lazy(() => import("./QuickFireChallenge"));

const PREMIUM_KEY = 'lifeInUkPremium';

// --- localStorage key versioning ---------------------------------------
// The study guide was restructured from 42 overlapping sections (495 cards)
// into 12 modules (453 cards). Both keys below are indexed BY SECTION POSITION
// ("<sectionIdx>-<cardIdx>" for read cards, "<sectionIdx>" for collapse state),
// so data written before the restructure now points at completely different
// cards — returning users would see read-ticks scattered over cards they had
// never opened. That is silent corruption, not a visible reset, so the keys are
// versioned to orphan the old values instead of migrating them.
//
// Bump the suffix again on any future change to section order or card indices.
// Do NOT version PREMIUM_KEY — paid users must keep access across restructures.
// Sibling keys bumped for the same reason: STREAKS_KEY (quickFireStats.js) and
// STORAGE_KEY (MockExam.js), both keyed by the now-renamed exam topic strings.
const READ_CARDS_KEY = 'lifeInUkReadCards.v2';
const COLLAPSED_SECTIONS_KEY = 'lifeInUkCollapsedSections.v2';
// Must stay identical to STORAGE_KEY in MockExam.js — MockExam writes results
// and this component reads them back for the home-page progress summary.
const MOCK_RESULTS_KEY = 'lifeInUkMockResults.v2';

// Success dialog shown after redeem code / payment
const PremiumSuccessModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-ink-50 rounded-2xl max-w-sm w-full p-8 shadow-lift-3 text-center">
        <div className="text-5xl mb-4">🎉</div>
        <h3 className="text-2xl font-bold text-ink-900 mb-2">Premium Unlocked!</h3>
        <p className="text-gray-600 dark:text-slate-300 mb-2">You now have full access to:</p>
        <ul className="text-sm text-gray-700 dark:text-slate-300 mb-6 space-y-1 text-left mx-auto inline-block">
          <li>✅ All 45 Mock Exams</li>
          <li>✅ Performance Dashboard & Analytics</li>
          <li>✅ Downloadable Offline Cheat Sheet</li>
          <li>✅ 5-Day Study Plan</li>
        </ul>
        <button
          onClick={onClose}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-bold transition shadow-md"
        >
          Start Studying 🚀
        </button>
      </div>
    </div>
  );
};

// Privacy Policy modal
const PrivacyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-ink-50 rounded-2xl max-w-lg w-full p-6 shadow-lift-3 max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">Privacy Policy</h3>
          <button onClick={onClose} className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 text-2xl leading-none">&times;</button>
        </div>
        <div className="text-sm text-gray-600 dark:text-slate-300 space-y-4">
          <p><strong>Last updated: August 2026</strong></p>
          <p>This Privacy Policy explains how <strong>Life in the UK Study Guide</strong> ("we", "us") handles your information when you use our website.</p>
          <h4 className="font-semibold text-gray-800 dark:text-slate-200">What we collect</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Payment information:</strong> When you purchase Premium, your payment is processed securely by <strong>Stripe</strong>. We do not store your card details. Stripe stores your email address to process the transaction.</li>
            <li><strong>Local data:</strong> Your exam results and premium status are stored in your browser's localStorage only. We do not store this on any server.</li>
            <li><strong>Contact form:</strong> If you contact us via the in-app form, your name, email, and message are sent to us via Formspree.</li>
            <li><strong>Free cheat sheet:</strong> If you request the free cheat sheet, we store your email address on our hosting provider (Netlify) so we can send you occasional study tips. We check that the address is a real, deliverable one and keep a single record per person. You can ask us to delete it at any time.</li>
          </ul>
          <h4 className="font-semibold text-gray-800 dark:text-slate-200">How we use it</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li>To process and verify your Premium purchase</li>
            <li>To respond to support enquiries</li>
            <li>We do not sell, share, or use your data for advertising</li>
          </ul>
          <h4 className="font-semibold text-gray-800 dark:text-slate-200">Your rights (GDPR)</h4>
          <p>You have the right to request deletion of any personal data we hold. Email us at <a href="mailto:help@lifeinukcoach.co.uk" className="text-indigo-600 dark:text-indigo-400 underline">help@lifeinukcoach.co.uk</a> and we will respond within 30 days.</p>
          <h4 className="font-semibold text-gray-800 dark:text-slate-200">Cookies</h4>
          <p>We use only essential browser localStorage. No tracking cookies or third-party analytics scripts are used beyond standard Netlify and Stripe integrations.</p>
          <p className="text-xs text-gray-400 dark:text-slate-500 pt-2">Questions? Contact us at <a href="mailto:help@lifeinukcoach.co.uk" className="text-indigo-600 dark:text-indigo-400 underline">help@lifeinukcoach.co.uk</a></p>
        </div>
        <button onClick={onClose} className="mt-6 w-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 py-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition font-medium">Close</button>
      </div>
    </div>
  );
};

// Terms of Service modal
const TermsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-ink-50 rounded-2xl max-w-lg w-full p-6 shadow-lift-3 max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">Terms of Service & Refund Policy</h3>
          <button onClick={onClose} className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 text-2xl leading-none">&times;</button>
        </div>
        <div className="text-sm text-gray-600 dark:text-slate-300 space-y-4">
          <p><strong>Last updated: July 2026</strong></p>
          <p>Welcome to <strong>Life in the UK Test Coach</strong> ("we", "us", "our"). By purchasing or using our services, you agree to these terms.</p>
          <h4 className="font-semibold text-gray-800 dark:text-slate-200">1. Digital Content & Access</h4>
          <p>Upon payment of the £7.99 premium fee, you are granted immediate, non-transferable access to advanced mock exams, offline cheat sheets, and personal metrics. This is a one-time charge with no recurring fees.</p>
          <h4 className="font-semibold text-gray-800 dark:text-slate-200">2. Refund & Pass Guarantee Policy</h4>
          <p>We stand by our material. We offer a <strong>100% Pass Guarantee</strong> under the following conditions:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>You must have completed and passed all 45 mock tests available on our platform prior to your official test date.</li>
            <li>If you still fail the official Life in the UK test, we will issue a full refund of your £7.99 purchase fee.</li>
            <li>To claim, email a copy of your official Home Office fail notification along with your purchase email address to <a href="mailto:help@lifeinukcoach.co.uk" className="text-indigo-600 dark:text-indigo-400 underline">help@lifeinukcoach.co.uk</a> within 90 days of purchase.</li>
          </ul>
          <h4 className="font-semibold text-gray-800 dark:text-slate-200">3. Intellectual Property & License Limitation</h4>
          <p>All test questions, system scripts, design assets, and compiled PDFs are intellectual property of our platform. Users may download files solely for personal educational use. Commercial redistribution or sharing account access is prohibited.</p>
          <h4 className="font-semibold text-gray-800 dark:text-slate-200">4. Limitation of Liability</h4>
          <p>We provide prep resources. Passing our mock exams does not guarantee passing the official Government test. We are not responsible for any official exam rescheduling fees or application costs.</p>
          <p className="text-xs text-gray-400 dark:text-slate-500 pt-2">Questions? Contact us at <a href="mailto:help@lifeinukcoach.co.uk" className="text-indigo-600 dark:text-indigo-400 underline">help@lifeinukcoach.co.uk</a></p>
        </div>
        <button onClick={onClose} className="mt-6 w-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 py-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition font-medium">Close</button>
      </div>
    </div>
  );
};

// Legal Disclaimer modal
const DisclaimerModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-ink-50 rounded-2xl max-w-lg w-full p-6 shadow-lift-3 max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">Legal Disclaimer & Copyright Notice</h3>
          <button onClick={onClose} className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 text-2xl leading-none">&times;</button>
        </div>
        <div className="text-sm text-gray-600 dark:text-slate-300 space-y-4">
          <p>
            <strong>lifeinukcoach.co.uk</strong> is an independent educational platform
            privately owned and operated. This website is <strong>not affiliated with,
              endorsed by, or connected to the UK Home Office or the UK Government</strong>.
          </p>
          <p>
            The practice questions, study guides, and mock exams provided on this platform
            are intended solely for educational preparation purposes. While we strive to
            replicate authentic exam conditions, passing these mock exams does not guarantee
            success on the official Life in the UK test.
          </p>
          <p>
            <strong>Public Sector Information:</strong> Materials on this website contain
            public sector information licensed under the
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 underline">
              Open Government Licence v3.0
            </a>.
            Official test guidelines and registration should always be verified via the official
            <a href="https://www.gov.uk" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 underline">GOV.UK portal</a>.
          </p>
          <p className="text-xs text-gray-400 dark:text-slate-500 pt-2">Questions? Contact us at <a href="mailto:help@lifeinukcoach.co.uk" className="text-indigo-600 dark:text-indigo-400 underline">help@lifeinukcoach.co.uk</a></p>
        </div>
        <button onClick={onClose} className="mt-6 w-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 py-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition font-medium">Close</button>
      </div>
    </div>
  );
};

// Cookie Notice Banner
const CookieBanner = () => {
  const [visible, setVisible] = useState(() => !safeGetItem("lifeInUkCookieAccepted"));

  // The banner is position:fixed, so it sits on top of whatever happens to be
  // at the bottom of the viewport. On 2026-09-15 that was the new "Start a
  // Free Mock Exam" CTA on /study-guide at 450x666 — the page's primary action
  // was completely hidden behind it until dismissed.
  //
  // A class on <body> lets any page reserve the space with a matching
  // padding-bottom rule (see index.css), which a component-local state cannot
  // do. Removed on accept and on unmount so it never leaks.
  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add('has-cookie-banner');
    return () => document.body.classList.remove('has-cookie-banner');
  }, [visible]);

  if (!visible) return null;

  const accept = () => {
    safeSetItem("lifeInUkCookieAccepted", "true");
    setVisible(false);
  };

  // Sits directly above the mobile tab bar as a single slim line. The previous
  // version was a four-line block ~380px tall that covered the lower half of a
  // 844px phone screen on every page until dismissed — it was the first thing
  // most visitors saw. The full policy lives on /privacy; this only needs to
  // disclose and offer dismissal.
  return (
    <div
      className="fixed bottom-16 md:bottom-4 left-3 right-3 md:left-auto md:right-4 md:max-w-sm bg-ink-900 pl-4 pr-3 py-2.5 rounded-xl shadow-lift-3 z-50 flex items-center gap-3"
      style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
      role="region"
      aria-label="Cookie notice"
    >
      <p className="text-xs leading-snug text-ink-300 flex-1">
        Essential local storage only. It saves your progress. No tracking.{' '}
        <Link to="/privacy" className="underline underline-offset-2 hover:text-ink-100">
          Details
        </Link>
      </p>
      <button
        onClick={accept}
        className="shrink-0 bg-action-600 hover:bg-action-700 text-white text-xs px-4 min-h-tap rounded-lg font-bold transition"
      >
        Got it
      </button>
    </div>
  );
};


// Gated wrapper for the /cheat-sheet route: shows a paywall for free users,
// otherwise renders the full cheat sheet with a working PDF download button.
function CheatSheetPage({ isPremium, onUnlockPremium, onDownloadPdf }) {
  if (!isPremium) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <div className="text-6xl mb-4">🔒</div>
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">Premium Cheat Sheet is locked</h2>
        <p className="text-slate-500 dark:text-slate-400 mb-6">
          Unlock the full visual quick-reference guide: timeline, government structure, key dates, monarchs, patron saints, British values, and more, plus a downloadable PDF version.
        </p>
        <button
          onClick={onUnlockPremium}
          className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-6 py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition"
        >
          Unlock Premium Insights (£7.99)
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-4xl mx-auto flex justify-end mb-4">
        <button
          onClick={onDownloadPdf}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-md transition"
        >
          📥 Download PDF
        </button>
      </div>
      <CheatSheet />
    </div>
  );
}

// The site header renders on every route. Its headline is the page's <h1> on
// the home page only — every other route (guides, study-guide topics, pricing)
// supplies its own <h1>, and rendering this one as a second <h1> above it gave
// those pages two competing top-level headings. That is both an SEO problem
// (prerender.mjs snapshots all routes, so the duplicate shipped to search
// engines) and a UX one: sub-pages opened under a full home-page hero, so the
// guide you clicked appeared ~400px down and looked like the wrong page.
//
// Mobile is ~88% of traffic and was bouncing at 87% in under 30s against a
// five-line header stack, so the home page keeps the headline plus one subhead
// to fit the primary CTA on the first mobile screen (see TrustStrip for the
// proof points moved below the fold). Off the home page the same wording stays
// for branding but is demoted to a <p> and the subhead is dropped, so the real
// page heading sits nearer the top.
function SiteHeroHeading() {
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  if (!isHome) {
    return (
      <p className="text-xl md:text-2xl font-extrabold text-ink-900 font-display">
        Life in the UK Test Practice
      </p>
    );
  }

  return (
    <>
      {/* The rotating greeting is the first line of the hero. It replaces no
          existing element and occupies one line that the hero already had as
          margin, so the headline, CTA and proof line stay exactly where they
          were — this hero is conversion-tuned to fit on one mobile screen and
          must not grow taller. The fixed min-height stops the swap between
          short and long greetings from reflowing the CTA below it. */}
      <p
        className="text-action-text font-display font-bold text-lg leading-none mb-2 flex items-center justify-center"
        style={{ minHeight: '1.5rem' }}
      >
        <Greeting />
      </p>
      <h1 className="text-3xl md:text-4xl font-extrabold text-ink-900 mb-2">
        Life in the UK Test Practice
      </h1>
      <p className="text-ink-700 font-medium">
        453 free flashcards and 45 full mock exams for British Citizenship and ILR 2026.
      </p>
      <div className="mt-5 flex justify-center">
        <Link
          to="/mock-exams"
          className="inline-flex items-center gap-2 bg-action-600 hover:bg-action-700 text-white text-base font-bold px-7 py-3.5 rounded-xl transition shadow-lift-2"
        >
          Start a Free Mock Exam
        </Link>
      </div>
      <p className="text-xs text-ink-500 mt-2">
        Free · No sign-up · No credit card required
      </p>
    </>
  );
}

// Remembers each page's scroll position across navigations (Link clicks and
// browser back/forward alike), so returning to a page you've visited before
// restores where you were instead of always snapping to the top.
const scrollPositions = new Map();

function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    const saved = scrollPositions.get(pathname);
    if (saved !== undefined) {
      window.scrollTo(0, saved);
    } else if (navigationType !== "POP") {
      window.scrollTo(0, 0);
    }

    return () => {
      scrollPositions.set(pathname, window.scrollY);
    };
  }, [pathname, navigationType]);

  useDocumentMeta({
    title: pathname === "/" ? "Life in the UK Mock Test [Updated 2026] | Free Flashcards" : undefined,
    description: pathname === "/" ? "Life in the UK Test practice with 453 free flashcards and 3 free mock exams, updated for 2026. Track your progress and prepare for British Citizenship and ILR." : undefined,
    path: pathname === "/" ? "/" : undefined,
  });

  return null;
}

function PromoBanner({ isPremium, onUnlockPremium }) {
  const { pathname } = useLocation();

  if (isPremium) return null;

  // The homepage variant is rendered below the first flashcard module rather
  // than in the header (see HomePromo), so a first-time visitor meets the free
  // product before a price. Everything else keeps the compact header pill.
  //
  // /study-guide is excluded for the same reason, added 2026-09-15: it is the
  // link shared into Facebook groups and the site's #1 landing page (417 users
  // in the first two weeks of September, more than the homepage). A £7.99 ask
  // was the FIRST thing a cold visitor read, before any free content — and the
  // page held attention for 13.7s against 383s on /mock-exams. The premium ask
  // still appears on that page, lower down, once the content has been seen.
  if (pathname === "/" || pathname === "/study-guide" || pathname === "/study-guide/") return null;

  return (
    <div className="mt-4 flex justify-center">
      <button
        onClick={onUnlockPremium}
        className="inline-flex items-center justify-center gap-2 bg-action-50 border border-action-100 text-action-text text-xs font-semibold px-4 py-4 min-h-tap leading-none rounded-full hover:bg-action-100 transition shadow-lift-1"
      >
        <IconLock className="w-4 h-4" />
        Unlock Premium (£7.99, one-off)
      </button>
    </div>
  );
}

// Proof points that used to sit in the header, where they pushed the primary
// CTA off the first mobile screen. Same claims, shown once the visitor has
// scrolled past the first module and is actually evaluating the product.
function TrustStrip() {
  return (
    <div className="mb-8 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-slate-500 dark:text-slate-400">
      <span>📊 1,080 practice questions</span>
      <span>📚 453 flashcards across 12 modules</span>
      <span>✓ Content reviewed: August 2026</span>
    </div>
  );
}

// Homepage premium offer. Deliberately placed after the first flashcard module
// instead of the header: at 87% mobile bounce inside 30s, asking for £7.99
// before the visitor has used anything was the first thing they saw.
function HomePromo({ isPremium, onUnlockPremium }) {
  if (isPremium) return null;

  return (
    <div className="mb-12 bg-action-50 border border-action-100 rounded-2xl p-6 max-w-lg mx-auto shadow-lift-1 text-center">
      <p className="text-xs text-action-text font-semibold mb-2">
        📊 The national first-time pass rate is around 70–75%: preparation makes the difference.
      </p>
      <p className="text-lg font-display font-bold text-ink-900 mb-1">
        Ready for the full test bank?
      </p>
      <p className="text-sm text-ink-600 mb-4 u-measure mx-auto">
        Premium unlocks all 45 mock exams, your 5-day study path, detailed analytics,
        and downloadable cheat sheets.
      </p>
      <button
        onClick={onUnlockPremium}
        className="inline-flex items-center justify-center bg-action-600 hover:bg-action-700 text-white text-sm px-6 min-h-tap rounded-xl font-bold transition shadow-lift-2"
      >
        Unlock Premium (£7.99, one-off)
      </button>
      <p className="text-xs text-ink-500 mt-2">
        One-time payment · no subscription
      </p>
    </div>
  );
}

// Nav pill/tab whose active state is derived from the current route rather
// than the legacy `view` state var, so it stays in sync for every page
// (including ones like Study Guide that don't call setView).
function NavPill({ to, activeClass, inactiveClass, onClick, children }) {
  const { pathname } = useLocation();
  const isActive = to === "/" ? pathname === "/" : pathname.startsWith(to);
  return (
    <Link to={to} onClick={onClick} className={isActive ? activeClass : inactiveClass}>
      {children}
    </Link>
  );
}


// Opens Quick-Fire from a URL. Quick-Fire is modal state in App with no route,
// so study-guide pages had no way to send anyone into it — they could only link
// to /mock-exams, a 45-minute commitment. `?quickfire=1` gives them a one-minute
// option. The param is stripped afterwards so a refresh or back-navigation does
// not reopen it.
// Opens Quick-Fire from anywhere WITHOUT navigating.
//
// The first attempt used a `?quickfire=1` link, which was wrong: it navigated to
// `/` first, so closing the modal left the visitor stranded on the homepage
// instead of back on the study-guide page they came from. Quick-Fire is a
// full-screen overlay rendered at App level and is available on every route, so
// there is no reason to change route at all.
//
// A CustomEvent lets any page open it in place. Modal state stays in App, and
// Cancel returns the visitor to exactly where they were.
function QuickFireOpener({ onOpen }) {
  useEffect(() => {
    const handler = () => onOpen();
    window.addEventListener('lifeinuk:open-quickfire', handler);
    return () => window.removeEventListener('lifeinuk:open-quickfire', handler);
  }, [onOpen]);
  return null;
}

export default function App() {
  const { isDark, toggleTheme } = useDarkMode();
  const [isPremium, setIsPremium] = useState(() => safeGetItem(PREMIUM_KEY) === "true");
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  // Which surface opened the paywall. premium_modal_open is fired centrally
  // (see the effect below) so that new entry points are counted automatically,
  // but that also means the event alone cannot say WHERE the ask came from —
  // a homepage banner click and a post-exam results click were indistinguishable
  // in GA4, which made "is the results-screen CTA working?" unanswerable.
  // Defaults to 'unknown' so an entry point that forgets to set it still
  // reports cleanly rather than dropping the event.
  const [premiumModalSource, setPremiumModalSource] = useState('unknown');

  // Opens the paywall and records the surface that asked for it.
  const openPremiumModal = useCallback((source = 'unknown') => {
    setPremiumModalSource(source);
    setShowPremiumModal(true);
  }, []);
  const [showPremiumSuccess, setShowPremiumSuccess] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemError, setRedeemError] = useState("");
  const [showQuickFire, setShowQuickFire] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showMobileMoreSheet, setShowMobileMoreSheet] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [mockResults, setMockResults] = useState(() => {
    try {
      return safeGetItem(MOCK_RESULTS_KEY, []);
    } catch {
      return [];
    }
  });

  // Derived, not stored: mockResults is the single source of truth, and this
  // must never drift from the value MockExam shows on its own results screen.
  const confidenceScore = calculateConfidenceScore(mockResults);

  // Tracking read flashcards (key: 'sectionIndex-cardIndex')
  const [readCards, setReadCards] = useState(() => {
    try {
      return safeGetItem(READ_CARDS_KEY, {});
    } catch {
      return {};
    }
  });

  // Tracking collapsed flashcard categories
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      return safeGetItem(COLLAPSED_SECTIONS_KEY, {});
    } catch {
      return {};
    }
  });

  // Which cards are currently flipped to their answer, keyed "<section>-<card>".
  // This lives here rather than inside FlashCard because cards unmount once they
  // scroll far enough offscreen (see LazyCardWrapper) — local state would reset
  // every time a flipped card scrolled away and came back. Deliberately not
  // persisted: a flipped card is a transient reading position, and restoring a
  // deck full of revealed answers on the next visit would defeat the point.
  const [flippedCards, setFlippedCards] = useState({});

  const handleToggleFlip = (sectionIdx, cardIdx, isFlipped) => {
    const key = `${sectionIdx}-${cardIdx}`;
    setFlippedCards((prev) => {
      if (isFlipped) return { ...prev, [key]: true };
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleToggleRead = (sectionIdx, cardIdx, isReadVal) => {
    const key = `${sectionIdx}-${cardIdx}`;
    const newReadCards = { ...readCards };
    if (isReadVal) {
      newReadCards[key] = true;
    } else {
      delete newReadCards[key];
    }
    setReadCards(newReadCards);
    safeSetItem(READ_CARDS_KEY, newReadCards);
  };

  const handleToggleCollapse = (sectionIdx) => {
    const newCollapsed = { ...collapsedSections };
    newCollapsed[sectionIdx] = !newCollapsed[sectionIdx];
    setCollapsedSections(newCollapsed);
    safeSetItem(COLLAPSED_SECTIONS_KEY, newCollapsed);
  };

  const getReadCount = (section, sectionIdx) => {
    return section.cards.reduce((sum, _, cardIdx) => {
      return sum + (readCards[`${sectionIdx}-${cardIdx}`] ? 1 : 0);
    }, 0);
  };


  const triggerConfetti = () => {
    confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    // Server-side session verification to prevent ?premium=true bypass
    if (sessionId) {
      fetch('/.netlify/functions/verifySession', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.isPremium) {
            // Read before the write below: a reload mid-verification would
            // otherwise re-fire purchase for a session already counted.
            const alreadyPremium = safeGetItem(PREMIUM_KEY) === 'true';
            safeSetItem(PREMIUM_KEY, 'true');
            setIsPremium(true);
            if (!alreadyPremium && window.gtag) {
              window.gtag('event', 'purchase', {
                transaction_id: sessionId,
                value: 7.99,
                currency: 'GBP',
                items: [{
                  item_id: 'premium_plan',
                  item_name: 'Life in the UK Premium Plan',
                  price: 7.99,
                  quantity: 1,
                }],
              });
            }
            triggerConfetti();
            setTimeout(triggerConfetti, 600);
            setShowPremiumSuccess(true);
          }
        })
        .catch((err) => console.error('Session verification error:', err))
        .finally(() => {
          window.history.replaceState({}, document.title, window.location.pathname);
        });
    } else if (urlParams.get('premium') === 'true') {
      // Clean query param if session_id is missing
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleSubscribe = async () => {
    if (isCheckoutLoading) return;
    setIsCheckoutLoading(true);
    if (window.gtag) window.gtag('event', 'begin_checkout', { value: 7.99, currency: 'GBP' });
    try {
      const response = await fetch('/.netlify/functions/createCheckout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        console.error('Checkout error response:', err);
        alert(err.error || 'Error starting checkout. Please try again.');
        setIsCheckoutLoading(false);
        return;
      }
      const session = await response.json();
      if (session.url) {
        window.location.href = session.url;
      } else {
        alert('Error starting checkout. Please try again.');
        setIsCheckoutLoading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Sorry, there was an error. Please contact support.');
      setIsCheckoutLoading(false);
    }
  };

  const handleRedeemCode = async () => {
    if (!redeemCode.trim()) return;

    // Local development bypass for testing
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const trimmedCode = redeemCode.trim();
    const devCodes = ['premium2026', 'devtest2026'];

    if (isLocalDev && devCodes.includes(trimmedCode.toLowerCase())) {
      safeSetItem(PREMIUM_KEY, 'true');
      setIsPremium(true);
      setRedeemError('');
      setShowPremiumModal(false);
      triggerConfetti();
      setTimeout(triggerConfetti, 600);
      setShowPremiumSuccess(true);
      return;
    }

    try {
      const res = await fetch('/.netlify/functions/validateRedeemCode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: trimmedCode }),
      });
      const data = await res.json();
      if (data.valid) {
        safeSetItem(PREMIUM_KEY, 'true');
        setIsPremium(true);
        setRedeemError('');
        setShowPremiumModal(false);
        // Not a purchase: a £0 purchase event would drag down conversion rate
        // and pollute revenue reporting.
        if (window.gtag) window.gtag('event', 'unlock_redeem_code');
        triggerConfetti();
        setTimeout(triggerConfetti, 600);
        setShowPremiumSuccess(true);
      } else {
        setRedeemError(data.error || 'Invalid code. Please contact us to get a valid code.');
      }
    } catch (err) {
      setRedeemError('Network error. Please try again.');
    }
  };

  // Called by PremiumModal after a successful Stripe email verification
  const handleRestoreAccess = () => {
    safeSetItem(PREMIUM_KEY, 'true');
    setIsPremium(true);
    setShowPremiumModal(false);
    triggerConfetti();
    setTimeout(triggerConfetti, 600);
    setShowPremiumSuccess(true);
  };

  // Fires once whenever the paywall becomes visible, from any of its entry
  // points. Without this, GA4 shows begin_checkout but nothing upstream of it,
  // so "nobody is seeing the offer" and "people see it and decline" are
  // indistinguishable. Tracked here rather than at each setShowPremiumModal
  // call site so new entry points are counted automatically.
  useEffect(() => {
    if (showPremiumModal && window.gtag) {
      // Parameter is `cta_source`, NOT `source`. `source` is a GA4 reserved
      // name that feeds traffic-source attribution: sending it here wrote
      // values like "exam_topbar" and "cheat_sheet_page" into sessionSource,
      // polluting the acquisition reports, and no custom dimension could be
      // registered against it. Found 2026-09-15 after the values showed up as
      // referrers. Never reuse GA4 reserved parameter names (source, medium,
      // campaign, term, content, page_title, page_location) for custom data.
      window.gtag('event', 'premium_modal_open', { cta_source: premiumModalSource });
    }
    // premiumModalSource is deliberately omitted from the dependency list: it is
    // set in the same tick as showPremiumModal, and including it would re-fire
    // the event if the source ever changed while the modal stayed open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPremiumModal]);

  // ----- Enhanced Premium Cheat Sheet -----
  const handleDownloadCheatSheet = async () => {
    if (!isPremium) {
      openPremiumModal('cheat_sheet_download');
      return;
    }

    // Loaded on click so the library is not in the main bundle. Both modules
    // are fetched in parallel; the chunk is cached after the first download.
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
    ]);

    const doc = new jsPDF();
    generateCheatSheet(doc, sections, autoTable);
  };

  return (
    <Router>
      <ScrollToTop />
      <QuickFireOpener onOpen={() => setShowQuickFire(true)} />
      <ErrorBoundary>
        <div className="min-h-screen bg-ink-100 p-4 md:p-8 pb-28 md:pb-8 font-sans transition-colors">

          <div className="max-w-6xl mx-auto">
            {/* Theme toggle is absolutely positioned (2026-09-15) rather than
                sitting in its own flex row. As a row it reserved ~60px of empty
                width-full space above the greeting on every page, which on
                mobile pushed the hero and its CTA down for no benefit. */}
            <header className="text-center mb-6 relative">
              <button
                onClick={toggleTheme}
                aria-label="Toggle dark mode"
                className="absolute right-0 top-0 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-ink-50 border border-ink-200 text-ink-600 hover:bg-ink-100 hover:text-ink-800 transition shadow-lift-1"
              >
                {isDark ? <IconSun className="w-5 h-5" /> : <IconMoon className="w-5 h-5" />}
              </button>
              <SiteHeroHeading />
              {/* Desktop nav: pill row (hidden on mobile, replaced by bottom tab bar) */}
              <nav className="hidden md:flex mt-4 flex-wrap justify-center gap-4">
                <NavPill
                  to="/"
                  activeClass="px-4 min-h-tap inline-flex items-center rounded-full font-semibold transition text-base bg-action-600 text-white"
                  inactiveClass="px-4 min-h-tap inline-flex items-center rounded-full font-medium transition text-base bg-ink-50 text-action-text border border-ink-200 hover:border-action-500"
                >
                  Home
                </NavPill>
                <NavPill
                  to="/mock-exams"
                  activeClass="px-4 min-h-tap inline-flex items-center rounded-full font-semibold transition text-base bg-action-600 text-white"
                  inactiveClass="px-4 min-h-tap inline-flex items-center rounded-full font-medium transition text-base bg-ink-50 text-action-text border border-ink-200 hover:border-action-500"
                >
                  Mock Exams
                </NavPill>
                <NavPill
                  to="/study-guide"
                  activeClass="px-4 min-h-tap inline-flex items-center rounded-full font-semibold transition text-base bg-action-600 text-white"
                  inactiveClass="px-4 min-h-tap inline-flex items-center rounded-full font-medium transition text-base bg-ink-50 text-action-text border border-ink-200 hover:border-action-500"
                >
                  Study Guide
                </NavPill>
                <NavPill
                  to="/ilr-guide"
                  activeClass="px-4 min-h-tap inline-flex items-center rounded-full font-semibold transition text-base bg-action-600 text-white"
                  inactiveClass="px-4 min-h-tap inline-flex items-center rounded-full font-medium transition text-base bg-ink-50 text-action-text border border-ink-200 hover:border-action-500"
                >
                  ILR Guide
                </NavPill>
                <button
                  onClick={() => { track('quickfire_open', { surface: 'desktop_nav' }); setShowQuickFire(true); }}
                  className="px-4 min-h-tap inline-flex items-center gap-1.5 rounded-full transition text-base bg-accent-600 hover:bg-accent-500 text-white shadow-lift-1 font-bold"
                >
                  <IconBolt className="w-4 h-4" />
                  Quick-Fire
                </button>

                {/* Pricing sits in the primary nav, not the More menu.
                    Only 10 of 1,204 visitors in the 28 days to 2026-08-24
                    reached /pricing at all — the offer was effectively hidden
                    behind an overflow menu. Naming the price here also does the
                    reassuring: £7.99 one-off reads as cheap, and saying so early
                    is more persuasive than concealing it until checkout. */}
                <NavPill
                  to="/pricing"
                  activeClass="px-4 min-h-tap inline-flex items-center rounded-full font-semibold transition text-base bg-action-600 text-white"
                  inactiveClass="px-4 min-h-tap inline-flex items-center rounded-full font-medium transition text-base bg-ink-50 text-action-text border border-ink-200 hover:border-action-500"
                >
                  Pricing
                </NavPill>

                {/* More menu - secondary items */}
                <div className="relative inline-block">
                  <button
                    onClick={() => { if (!showMoreMenu) track('more_menu_open', { surface: 'desktop' }); setShowMoreMenu(!showMoreMenu); }}
                    className="px-4 min-h-tap inline-flex items-center rounded-full font-medium transition text-base bg-ink-50 text-ink-600 border border-ink-200 hover:border-action-500"
                  >
                    ☰ More
                  </button>
                  {showMoreMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
                      <div className="absolute right-0 top-full mt-2 z-50 bg-ink-50 rounded-2xl shadow-lift-3 border border-ink-200 p-2 min-w-[220px] space-y-1">
                        <Link
                          to="/cheat-sheet"
                          onClick={() => setShowMoreMenu(false)}
                          className="flex items-center gap-2 w-full text-left px-4 py-2.5 min-h-tap rounded-xl text-sm font-medium text-ink-700 hover:bg-action-50 hover:text-action-text transition"
                        >
                          {isPremium ? "📖 View Cheat Sheet" : "✨ Unlock Cheat Sheet"}
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </nav>

              {/* Mobile nav: fixed bottom tab bar.

                  Icons are inline SVG rather than emoji: emoji render as
                  platform-specific bitmaps, so the same bar looked different
                  on every Android/iOS build and two tabs (Cards, Guide) drew
                  the identical 📚 glyph. The SVGs inherit currentColor, so an
                  active tab tints icon and label together. */}
              <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-ink-50 border-t border-ink-200 flex items-stretch justify-around shadow-[0_-2px_10px_rgba(42,37,31,0.07)]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
                <NavPill
                  to="/"
                  activeClass="flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-tap text-xs font-semibold transition text-action-text"
                  inactiveClass="flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-tap text-xs font-medium transition text-ink-500"
                >
                  <IconCards className="w-6 h-6" />
                  Home
                </NavPill>
                <NavPill
                  to="/mock-exams"
                  activeClass="flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-tap text-xs font-semibold transition text-action-text"
                  inactiveClass="flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-tap text-xs font-medium transition text-ink-500"
                >
                  <IconExam className="w-6 h-6" />
                  Exams
                </NavPill>
                <NavPill
                  to="/study-guide"
                  activeClass="flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-tap text-xs font-semibold transition text-action-text"
                  inactiveClass="flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-tap text-xs font-medium transition text-ink-500"
                >
                  <IconGuide className="w-6 h-6" />
                  Guide
                </NavPill>
                <button
                  onClick={() => { track('quickfire_open', { surface: 'mobile_tab' }); setShowQuickFire(true); }}
                  className="flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-tap text-xs font-semibold text-accent-600 transition"
                >
                  <IconBolt className="w-6 h-6" />
                  Quick-Fire
                </button>
                <button
                  onClick={() => { track('more_menu_open', { surface: 'mobile' }); setShowMobileMoreSheet(true); }}
                  className="flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-tap text-xs font-medium text-ink-500 transition"
                >
                  <IconMore className="w-6 h-6" />
                  More
                </button>
              </nav>

              {/* Mobile "More" bottom sheet */}
              {showMobileMoreSheet && (
                <div className="md:hidden fixed inset-0 z-50">
                  <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowMobileMoreSheet(false)} />
                  <div className="absolute bottom-0 left-0 right-0 bg-ink-50 rounded-t-2xl shadow-lift-3 p-4 pb-8 space-y-1">
                    <div className="w-10 h-1 bg-ink-300 rounded-full mx-auto mb-3" />
                    <Link
                      to="/pricing"
                      onClick={() => setShowMobileMoreSheet(false)}
                      className="flex items-center justify-between gap-3 px-4 py-3 min-h-tap rounded-xl text-sm font-semibold text-ink-900 bg-action-50 border border-action-100 hover:bg-action-100 transition"
                    >
                      <span>Pricing</span>
                      <span className="text-action-text font-bold">£7.99 one-off</span>
                    </Link>
                    <Link
                      to="/ilr-guide"
                      onClick={() => setShowMobileMoreSheet(false)}
                      className="flex items-center gap-3 px-4 py-3 min-h-tap rounded-xl text-sm font-medium text-ink-700 hover:bg-action-50 hover:text-action-text transition"
                    >
                      ILR Guide
                    </Link>
                    <Link
                      to="/cheat-sheet"
                      onClick={() => setShowMobileMoreSheet(false)}
                      className="flex items-center gap-3 px-4 py-3 min-h-tap rounded-xl text-sm font-medium text-ink-700 hover:bg-action-50 hover:text-action-text transition"
                    >
                      {isPremium ? "📖 View Cheat Sheet" : "✨ Unlock Cheat Sheet"}
                    </Link>
                    <div className="px-4 py-3">
                      <TestDatePicker />
                    </div>
                  </div>
                </div>
              )}

              <PromoBanner isPremium={isPremium} onUnlockPremium={() => openPremiumModal('promo_banner')} />
            </header>

            <Routes>
              <Route path="/" element={(
                <>
                  {/* Readiness panel — shown once there are results to show.
                      Replaces a "sneak peek" progress graph that withheld the
                      confidence score as a premium tease. That was backwards:
                      the score is the number that tells someone they are NOT
                      ready yet, which is the most persuasive reason to want
                      more exams. Withholding it hid the reason to buy.
                      Detailed topic breakdown and the study plan stay premium.

                      Layout mirrors what the competitor does well (one bounded
                      card, stat tiles, test-date countdown) without their ads. */}
                  {mockResults.length >= 2 && (
                    <div className="mb-8 bg-ink-50 rounded-2xl border border-ink-200 p-5 sm:p-6 shadow-lift-1">
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <h3 className="text-lg font-bold text-ink-900">Your readiness</h3>
                        <Link
                          to="/mock-exams"
                          className="text-xs font-bold text-action-text hover:opacity-80 transition shrink-0"
                        >
                          Take another →
                        </Link>
                      </div>

                      {/* Stat tiles. tabular-nums keeps the figures aligned as
                          they change between renders. */}
                      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
                        <div className="rounded-xl bg-action-50 border border-action-100 p-3 text-center">
                          <p className="text-2xl sm:text-3xl font-extrabold text-action-text tabular-nums leading-tight">
                            {confidenceScore}%
                          </p>
                          <p className="text-[11px] font-semibold text-ink-600 mt-0.5">Confidence</p>
                        </div>
                        <div className="rounded-xl bg-ink-100 border border-ink-200 p-3 text-center">
                          <p className="text-2xl sm:text-3xl font-extrabold text-ink-900 tabular-nums leading-tight">
                            {Math.max(...mockResults.map(r => r.score))}%
                          </p>
                          <p className="text-[11px] font-semibold text-ink-600 mt-0.5">Best score</p>
                        </div>
                        <div className="rounded-xl bg-ink-100 border border-ink-200 p-3 text-center">
                          <p className="text-2xl sm:text-3xl font-extrabold text-ink-900 tabular-nums leading-tight">
                            {mockResults.length}
                          </p>
                          <p className="text-[11px] font-semibold text-ink-600 mt-0.5">
                            {mockResults.length === 1 ? 'Exam taken' : 'Exams taken'}
                          </p>
                        </div>
                      </div>

                      {/* Plain-language verdict. 75% is the official pass mark. */}
                      <p className={`text-sm font-semibold mb-4 ${confidenceScore >= 75 ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-500'}`}>
                        {confidenceScore >= 75
                          ? '⭐ On track. You are scoring above the 75% pass mark.'
                          : '📚 Not ready yet. You need 75% to pass, so keep practising.'}
                      </p>

                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="w-full md:max-w-xs">
                          <ProgressGraph results={mockResults} maxResults={5} />
                        </div>
                        <div className="flex-1 space-y-3">
                          <TestDatePicker />
                          {!isPremium && (
                            <button
                              onClick={() => openPremiumModal('home_readiness')}
                              className="inline-flex items-center gap-1.5 text-xs text-action-text hover:opacity-80 font-semibold transition"
                            >
                              <IconLock className="w-3.5 h-3.5" />
                              See which topics are letting you down
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mb-4 text-center text-sm text-gray-500 dark:text-slate-400">
                    📚 Browse all flashcards by topic below • Tap any card to flip
                  </div>
                  {sections.map((section, i) => {

                    const readCount = getReadCount(section, i);
                    const totalCount = section.cards.length;
                    const isAllRead = readCount === totalCount;
                    const isCollapsed = !!collapsedSections[i];

                    return (
                      <Fragment key={i}>
                      <section className="mb-12 bg-ink-50 rounded-2xl border border-ink-200 p-6 shadow-lift-1">
                        <div
                          onClick={() => handleToggleCollapse(i)}
                          className="flex justify-between items-center cursor-pointer pb-2 border-b-2 border-slate-100 dark:border-slate-800 mb-6 select-none"
                        >
                          <div className="flex items-center gap-3">
                            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                              {section.title}
                            </h2>
                            <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold transition ${isAllRead
                              ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
                              : 'bg-action-50 text-action-text border border-action-100'
                              }`}>
                              {readCount} / {totalCount} read {isAllRead && "🎉"}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleCollapse(i);
                            }}
                            className="text-xs font-bold text-action-text hover:opacity-80 transition bg-action-50 hover:bg-action-100 px-3 min-h-tap inline-flex items-center rounded-lg border border-action-100 shadow-lift-1"
                          >
                            {isCollapsed ? "Expand 📂" : "Collapse 📁"}
                          </button>
                        </div>

                        {/* Horizontal rail, not a grid (changed 2026-09-15).
                            As a vertical grid, 20 cards per module x 12 modules meant a
                            first-time visitor had to scroll past ~240 cards to see what the
                            site contained, and /study-guide held attention for only 13.7s.
                            One swipeable row per module keeps every module title within a
                            screen or two, so the breadth is visible without the depth.
                            snap-x makes it land on card boundaries rather than mid-card. */}
                        {!isCollapsed && (
                          <div className="-mx-6 px-6 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 u-no-scrollbar">
                            {section.cards.map((card, idx) => {
                              const isRead = !!readCards[`${i}-${idx}`];
                              const isFlipped = !!flippedCards[`${i}-${idx}`];
                              return (
                                <div key={idx} className="snap-start shrink-0 w-[78%] sm:w-64 lg:w-72">
                                <LazyCardWrapper>
                                  <FlashCard
                                    card={card}
                                    index={idx + i * 100}
                                    isRead={isRead}
                                    onToggleRead={(isReadVal) => handleToggleRead(i, idx, isReadVal)}
                                    flip={isFlipped}
                                    onFlip={(isFlippedVal) => handleToggleFlip(i, idx, isFlippedVal)}
                                  />
                                </LazyCardWrapper>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </section>
                      {/* Offer and proof points sit after the first module so the
                          free product is what a first-time mobile visitor meets. */}
                      {/* Moved from after module 1 to after module 2 on
                          2026-09-15. When each module was a tall vertical grid,
                          one module filled the screen and this block landed
                          naturally. As horizontal rails a module is only ~300px
                          tall, so breaking after the first one interrupted the
                          browsing before it had started. The test-date picker is
                          not repeated here — it now lives in the readiness panel
                          at the top for anyone who has taken an exam. */}
                      {i === 1 && (
                        <>
                          <HomePromo isPremium={isPremium} onUnlockPremium={() => openPremiumModal('home_promo')} />
                          {mockResults.length < 2 && (
                            <div className="mb-8 flex justify-center">
                              <TestDatePicker />
                            </div>
                          )}
                          <TrustStrip />
                        </>
                      )}
                      </Fragment>
                    );
                  })}
                </>
              )} />
              <Route path="/mock-exams" element={(
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 dark:text-slate-500">Loading mock exams...</div>}>
                  <MockExam
                    isPremium={isPremium}
                    setIsPremium={setIsPremium}
                    onUnlockPremium={(source) => openPremiumModal(source || 'mock_exam')}
                    onResultsUpdate={(results) => setMockResults(results)}
                  />
                </Suspense>
              )} />
              <Route path="/pricing" element={<Pricing onUnlockPremium={() => openPremiumModal('pricing_page')} isPremium={isPremium} />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/cheat-sheet" element={(
                <CheatSheetPage
                  isPremium={isPremium}
                  onUnlockPremium={() => openPremiumModal('cheat_sheet_page')}
                  onDownloadPdf={handleDownloadCheatSheet}
                />
              )} />
              <Route path="/free-cheat-sheet" element={<FreeCheatSheet />} />
              <Route path="/study-guide" element={<StudyGuideIndex />} />
              <Route path="/study-guide/british-history" element={<BritishHistory />} />
              <Route path="/study-guide/monarchs-timeline" element={<MonarchsTimeline />} />
              <Route path="/study-guide/people-matrix" element={<PeopleMatrix />} />
              <Route path="/study-guide/inventions-timeline" element={<InventionsTimeline />} />
              <Route path="/study-guide/key-dates" element={<KeyDatesTimeline />} />
              <Route path="/study-guide/government-and-law" element={<GovernmentAndLaw />} />
              <Route path="/study-guide/elections-cheat-sheet" element={<ElectionsCheatSheet />} />
              <Route path="/study-guide/devolved-nations" element={<DevolvedNations />} />
              <Route path="/study-guide/court-hierarchy" element={<CourtHierarchy />} />
              <Route path="/study-guide/culture-and-traditions" element={<CultureAndTraditions />} />
              <Route path="/study-guide/patron-saints" element={<PatronSaintsAndSymbols />} />
              <Route path="/study-guide/festivals-and-celebrations" element={<FestivalsAndCelebrations />} />
              <Route path="/study-guide/sports-and-traditions" element={<SportsAndTraditions />} />
              <Route path="/study-guide/modern-britain" element={<ModernBritain />} />
              <Route path="/study-guide/human-rights" element={<HumanRightsAndCitizenship />} />
              <Route path="/study-guide/taxation-and-driving" element={<TaxationAndDriving />} />
              <Route path="/study-guide/britain-in-the-world" element={<BritainInTheWorld />} />
              <Route path="/ilr-guide" element={<ILRGuide />} />
              {/* Dynamic immigration guide routes */}
              <Route path="/british-citizenship-guide" element={<GuidePage guide={guideBySlug["british-citizenship-guide"]} />} />
              <Route path="/skilled-worker-ilr" element={<GuidePage guide={guideBySlug["skilled-worker-ilr"]} />} />
              <Route path="/spouse-visa-ilr" element={<GuidePage guide={guideBySlug["spouse-visa-ilr"]} />} />
              <Route path="/global-talent-ilr" element={<GuidePage guide={guideBySlug["global-talent-ilr"]} />} />
              <Route path="/long-residence-ilr" element={<GuidePage guide={guideBySlug["long-residence-ilr"]} />} />
              <Route path="/english-requirement" element={<GuidePage guide={guideBySlug["english-requirement"]} />} />
              <Route path="/life-in-uk-requirement" element={<GuidePage guide={guideBySlug["life-in-uk-requirement"]} />} />
              <Route path="/ukvcas-appointment" element={<GuidePage guide={guideBySlug["ukvcas-appointment"]} />} />
              <Route path="/evisa-explained" element={<GuidePage guide={guideBySlug["evisa-explained"]} />} />
              <Route path="/citizenship-ceremony" element={<GuidePage guide={guideBySlug["citizenship-ceremony"]} />} />
              <Route path="/british-passport-application" element={<GuidePage guide={guideBySlug["british-passport-application"]} />} />
              <Route path="/dual-citizenship" element={<GuidePage guide={guideBySlug["dual-citizenship"]} />} />
              <Route path="/skilled-worker-redundancy" element={<GuidePage guide={guideBySlug["skilled-worker-redundancy"]} />} />
              {/* 404 catch-all route */}
              <Route path="*" element={(
                <div className="text-center py-20">
                  <div className="text-7xl mb-4">🔍</div>
                  <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">Page Not Found</h2>
                  <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                    The page you're looking for doesn't exist or has been moved.
                  </p>
                  <Link
                    to="/"
                    className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-700 transition shadow-md"
                  >
                    ← Back to Home
                  </Link>
                </div>
              )} />
            </Routes>

            {/* Verifiable product facts, shown to everyone at the bottom */}
            <TestimonialsCarousel />

            {/* Global Footer (shows on all views) */}
            <footer className="text-center py-10 mt-12 border-t border-ink-200 text-ink-500 text-sm space-y-4">
              <p className="font-semibold text-ink-600">🎓 Based on official Life in the UK Handbook (3rd edition) & mock test patterns. Good luck! 🇬🇧</p>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs font-medium [&>*]:py-3">
                <a href="mailto:help@lifeinukcoach.co.uk" className="text-action-text hover:text-action-text transition">✉️ Help & Support (help@lifeinukcoach.co.uk)</a>
                <Link to="/ilr-guide" className="text-action-text hover:text-action-text transition">🇬🇧 ILR & Citizenship Guide</Link>
                <button onClick={() => setShowTermsModal(true)} className="text-action-text hover:text-action-text transition">📜 Terms of Service & Refund Guarantee</button>
                <button onClick={() => setShowPrivacyModal(true)} className="text-action-text hover:text-action-text transition">🔒 Privacy Policy</button>
                <button onClick={() => setShowDisclaimerModal(true)} className="text-action-text hover:text-action-text transition">⚖️ Legal Disclaimer</button>
                <button onClick={() => openPremiumModal('restore_premium')} className="text-action-text hover:text-action-text transition">⭐ Restore Premium</button>
              </div>
              {/* Sitewide links into the immigration guide cluster. Those pages
                  carry most of the site's search impressions but were reachable
                  only from the sitemap and one link on /study-guide, so no
                  internal link equity flowed to them from the pages that
                  actually rank. Limited to the highest-impression guides — a
                  footer listing all fourteen would dilute the signal it exists
                  to send. The full set is on /study-guide. */}
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs pt-2 border-t border-ink-200 max-w-3xl mx-auto [&_a]:py-3 [&_a]:inline-block [&_span]:py-3">
                <span className="text-ink-500 font-semibold py-2 inline-block">Guides:</span>
                <Link to="/skilled-worker-ilr" className="text-ink-600 hover:text-action-text hover:underline transition">Skilled Worker ILR</Link>
                <Link to="/spouse-visa-ilr" className="text-ink-600 hover:text-action-text hover:underline transition">Spouse Visa ILR</Link>
                <Link to="/long-residence-ilr" className="text-ink-600 hover:text-action-text hover:underline transition">10-Year Long Residence</Link>
                <Link to="/british-citizenship-guide" className="text-ink-600 hover:text-action-text hover:underline transition">British Citizenship</Link>
                <Link to="/citizenship-ceremony" className="text-ink-600 hover:text-action-text hover:underline transition">Citizenship Ceremony</Link>
                <Link to="/english-requirement" className="text-ink-600 hover:text-action-text hover:underline transition">English Requirement</Link>
                <Link to="/ukvcas-appointment" className="text-ink-600 hover:text-action-text hover:underline transition">Biometric Appointment</Link>
                <Link to="/study-guide" className="text-action-text font-semibold hover:underline transition">All guides →</Link>
              </div>
              <div className="text-xs text-ink-500 max-w-2xl mx-auto space-y-2 pt-2 border-t border-ink-200">
                <p>This website is an independent educational platform. It is <strong>not affiliated with, endorsed by, or connected to the UK Home Office or the UK Government</strong>.</p>
                <p>Contains public sector information licensed under the <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="underline hover:text-ink-700 transition">Open Government Licence v3.0</a>.</p>
              </div>
            </footer>
          </div>

          <PremiumModal
            isOpen={showPremiumModal}
            onClose={() => setShowPremiumModal(false)}
            redeemCode={redeemCode}
            setRedeemCode={setRedeemCode}
            redeemError={redeemError}
            onRedeem={handleRedeemCode}
            onSubscribe={handleSubscribe}
            onRestoreAccess={handleRestoreAccess}
          />

          <PremiumSuccessModal
            isOpen={showPremiumSuccess}
            onClose={() => setShowPremiumSuccess(false)}
          />

          <PrivacyModal
            isOpen={showPrivacyModal}
            onClose={() => setShowPrivacyModal(false)}
          />

          <TermsModal
            isOpen={showTermsModal}
            onClose={() => setShowTermsModal(false)}
          />

          <DisclaimerModal
            isOpen={showDisclaimerModal}
            onClose={() => setShowDisclaimerModal(false)}
          />

          <CookieBanner />

          {showQuickFire && (
            <Suspense fallback={null}>
              <QuickFireChallenge
                onClose={() => setShowQuickFire(false)}
                isPremium={isPremium}
                onUnlockPremium={(source) => openPremiumModal(source || 'quick_fire')}
              />
            </Suspense>
          )}
        </div>
      </ErrorBoundary>
    </Router>
  );
}